/**
 * JSON-LD schema builders for Petals & Promises.
 * Imported by SEO component or pages to attach rich structured data.
 */

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://petalspromises.com";

const ROUTE_LABELS = {
  "/": "Home",
  "/about": "About",
  "/services": "Services",
  "/celebrations": "Celebrations",
  "/venues": "Venues",
  "/checklist": "Checklist",
  "/portfolio": "Portfolio",
  "/packages": "Packages",
  "/process": "Process",
  "/blogs": "Blogs",
  "/contact": "Contact",
};

/**
 * Build a BreadcrumbList for any path. Splits on "/" and labels each segment.
 * Special-cased to expand /blogs/<slug> into Home > Blogs > <slug>.
 */
export function buildBreadcrumb(path = "/", overrideLast) {
  const parts = path.split("/").filter(Boolean);
  const items = [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
  ];
  let acc = "";
  parts.forEach((seg, i) => {
    acc += `/${seg}`;
    const isLast = i === parts.length - 1;
    const labelKey = `/${seg}`;
    const label = isLast && overrideLast
      ? overrideLast
      : ROUTE_LABELS[labelKey] || decodeURIComponent(seg).replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    items.push({
      "@type": "ListItem",
      position: i + 2,
      name: label,
      item: `${SITE_URL}${acc}`,
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

export function buildServiceSchema(name, description, urlPath) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE_URL}${urlPath}`,
    provider: {
      "@type": "Organization",
      name: "Petals & Promises",
      url: SITE_URL,
    },
    areaServed: {
      "@type": "Country",
      name: "India",
    },
  };
}

export function buildItemListSchema(items, urlPath) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    url: `${SITE_URL}${urlPath}`,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: it.url ? `${SITE_URL}${it.url}` : undefined,
      description: it.description,
    })),
  };
}

export function buildFAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
  };
}

export function buildArticleSchema({ title, description, image, datePublished, urlPath, author = "Petals & Promises" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image,
    datePublished,
    dateModified: datePublished,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}${urlPath}`,
    },
    author: { "@type": "Organization", name: author },
    publisher: {
      "@type": "Organization",
      name: "Petals & Promises",
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_premium-celebrations-14/artifacts/ne75w843_Asset%201%4018x.png",
      },
    },
  };
}

/** Reusable FAQs surfaced on Contact + Services pages */
export const SITE_FAQS = [
  {
    question: "Where is Petals & Promises based and which cities do you serve?",
    answer: "We have studios in Sector 51, Gurgaon and Sector 74, Chandigarh, and we plan and execute events across Delhi NCR, Punjab, Himachal, Jaipur, Udaipur, Goa, and destination weddings worldwide.",
  },
  {
    question: "What kinds of events do you plan?",
    answer: "Luxury weddings, destination weddings, social celebrations (anniversaries, birthdays, milestone parties, baby showers), corporate events (launches, conferences, gala dinners), and devotional gatherings (Mata Ki Chowki, Jagran, pujas).",
  },
  {
    question: "How early should we engage a wedding planner?",
    answer: "Ideally 6 to 9 months before the wedding for full planning, and at least 90 days before for partial planning. For destination weddings we recommend a 9 to 12 month runway.",
  },
  {
    question: "Do you help with venue shortlisting?",
    answer: "Yes. We have a curated network of luxury hotels, palace resorts, banquet halls, farmhouses and offbeat venues. Tell us your guest count, dates and vision and we'll send a shortlist hand-picked for you.",
  },
  {
    question: "How do your packages work?",
    answer: "We offer five senior-led packages — Essential Planning, Signature Event Management, Luxury Wedding Experience, Corporate Experience Suite, and Devotional Event Suite — and tailor scope, pricing and team to your event.",
  },
  {
    question: "How quickly will you respond to my inquiry?",
    answer: "Within one business day. You'll receive a curated next step, a few relevant references from our portfolio, and a sensible budget conversation.",
  },
];
